import React, { createContext, useState, useContext, useCallback, useMemo, ReactNode } from 'react';
import { LinearProgress, Box, Typography } from '@mui/material';

// Define Context Type
interface LoadingContextType {
  loading: boolean;
  showLoading: (message?: string) => void;
  hideLoading: () => void;
}

// Create Context
const LoadingContext = createContext<LoadingContextType | undefined>(undefined);

// Custom Hook
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

// Provider Props Type
interface LoadingProviderProps {
  children: ReactNode;
}

// Provider Component
export const LoadingProvider: React.FC<LoadingProviderProps> = ({ children }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [text, setText] = useState<string>('');

  const showLoading = useCallback((message: string = 'Loading...') => {
    setText(message);
    setLoading(true);
  }, []);

  const hideLoading = useCallback(() => {
    setLoading(false);
    setText('');
  }, []);

  const contextValue = useMemo(
    () => ({ loading, showLoading, hideLoading }),
    [loading, showLoading, hideLoading],
  );

  return (
    <LoadingContext.Provider value={contextValue}>
      {children}
      {loading && <LoadingComponent text={text} />}
    </LoadingContext.Provider>
  );
};

// Loading Component
interface LoadingComponentProps {
  text: string;
}

const LoadingComponent: React.FC<LoadingComponentProps> = ({ text }) => {
  return (
    <React.Fragment>
      <Box
        sx={{
          zIndex: 2147483647,
          position: 'fixed',
          width: '100%',
          height: '100vh !important',
          background: 'rgba(255,255,255,0.6)',
          top: 0,
          left: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          textAlign: 'center',
          flexDirection: 'column',
        }}
      >
        {/* Dynamic Loading Text */}
        <Typography
          variant='h5'
          sx={{
            fontWeight: 'bold',
            color: '#333',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            animation: 'typing 4s steps(30) infinite, blink 0.8s infinite',
          }}
        >
          {text}
        </Typography>
      </Box>

      {/* Progress Bar */}
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          width: '100%',
          zIndex: 2147483647,
          display: 'grid',
          alignItems: 'flex-end',
        }}
      >
        <LinearProgress sx={{ height: 8 }} />
      </Box>
    </React.Fragment>
  );
};
